/**
 * Login itself is a redirect to the API's Okta flow (/auth/back); this page
 * only shows the last error code and the optional info banner.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { PLATFORM } from '@/shared/constants';
import { AUTH_BASE } from '@/shared/api/client';
import { consumeLoginErrorCode } from '@/features/auth/session';
import { hardNavigate } from '@/shared/lib/navigation';
import { htmlToLines } from '@/shared/lib/html-text';
import logo from '@/assets/images/logo.png';
import background from '@/assets/images/background.jpg';
import {
  infoMessageQueryOptions,
  isDisplayedOnBackOffice,
} from '@/features/info/info-message';

const LEVEL_CLASSES: Record<string, string> = {
  red: 'text-[#d50000] border-[#d50000]',
  orange: 'text-[#ff6d00] border-[#ff6d00]',
  green: 'text-[#00c853] border-[#00c853]',
  grey: 'text-[#757575] border-[#757575]',
};

export function LoginPage() {
  const { t } = useTranslation();
  const errorCode = useMemo(() => consumeLoginErrorCode(), []);
  const { data: info } = useQuery(infoMessageQueryOptions(PLATFORM.ABCDIOR));
  const banner = isDisplayedOnBackOffice(info) ? info : null;
  const level = banner?.level ?? 'grey';

  return (
    <div
      className="relative h-full min-h-screen w-auto bg-cover bg-center"
      style={{ backgroundImage: `url(${background})` }}
    >
      <img
        src={logo}
        alt="Dior"
        className="absolute left-1/2 top-[5%] h-[50px] w-auto -translate-x-1/2 -translate-y-1/2"
      />
      <div className="absolute left-1/2 top-1/2 flex h-[500px] w-[400px] -translate-x-1/2 -translate-y-1/2 flex-col justify-center bg-white text-center">
        <div className="text-[60px]">
          <strong>{t('login.welcome')}</strong>
        </div>
        <div className="-mt-[15px] mb-[30px] text-[15px] text-[#aba7a7]">
          {t('login.pleaseLogIn')}
        </div>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            hardNavigate(`${AUTH_BASE}/back`);
          }}
          data-testid="login-form"
        >
          <input
            type="submit"
            value={t('button.login')}
            className="mb-4 h-[50px] w-[220px] cursor-pointer bg-[#343a40] text-[15px] text-white hover:bg-[#23272b]"
            data-testid="login-submit-button"
          />
        </form>
        {errorCode && (
          // The translation may carry <br> (login.errorMessage does): the
          // breaks are kept, any other markup is rendered as text.
          <div
            className="-mt-[5px] mb-[10px] text-[14px] text-[rgb(228,0,0)]"
            role="alert"
            data-testid="login-error"
          >
            {htmlToLines(t(`login.${errorCode}`)).map((line, index) => (
              <span key={`${index}-${line}`}>
                {index > 0 && <br />}
                {line}
              </span>
            ))}
          </div>
        )}
        {banner?.message && (
          <div
            className={`mx-[15px] mt-5 whitespace-pre-line break-words border p-[10px] ${LEVEL_CLASSES[level] ?? LEVEL_CLASSES.grey}`}
            role="status"
            data-testid="login-info-message"
          >
            {level === 'green' && (
              <FontAwesomeIcon icon={faCheck} className="mr-[5px]" />
            )}
            {(level === 'red' || level === 'orange') && (
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="mr-[5px]"
              />
            )}
            {banner.message}
          </div>
        )}
      </div>
    </div>
  );
}
